import {Card, Column, Label, Table, DataStatus, Tabs} from 'platform/components';
import {Heading, HStack, Icon, Space, VStack} from 'platform/foundation';

import {useGetDefinitionTemplateListQuery, useGetDocumentGroupListQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {SettingsTemplate} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {TemplateTableRow} from './components/TemplateTableRow';
import {useGetDocumentKindActions} from './hooks/useGetDocumentKindActions';
import {createDocumentTemplatesStructure} from './utils/createDocumentTemplatesStructure';

const COLUMNS: Column[] = [
  {width: '10%', element: <Label>{i18n.t('entity.document.labels.type')}</Label>},
  {width: '25%', element: <Label>{i18n.t('entity.document.labels.templateFile')}</Label>},
  {width: '20%', element: <Label>{i18n.t('entity.document.labels.templateTitle')}</Label>},
  {width: '10%', element: <Label>{i18n.t('entity.document.labels.lastUpdate')}</Label>},
  {width: '10%', element: <Label>{i18n.t('entity.document.labels.addedBy')}</Label>},
  {width: '15%', element: <Label>{i18n.t('entity.document.labels.note')}</Label>},
];

export function DocumentTemplates() {
  const {
    data: definitionTemplateList,
    isLoading: isLoadingDefinitionTemplateList,
    isError: isErrorDefinitionTemplateList,
  } = useGetDefinitionTemplateListQuery({});

  const {
    data: documentGroupList,
    isLoading: isLoadingDocumentGroupList,
    isError: isErrorDocumentGroupList,
  } = useGetDocumentGroupListQuery();

  const [hasSalesLicense, canReadPneuhotel, canReadArticle, canReadServiceCase] = usePermissions({
    permissionKeys: ['businessCaseRead', 'pneuhotelRead', 'articleRead', 'serviceCaseRead'],
  });

  const isLoading = isLoadingDefinitionTemplateList || isLoadingDocumentGroupList;
  const isError = isErrorDefinitionTemplateList || isErrorDocumentGroupList;

  const [getDocumentKindActions] = useGetDocumentKindActions();

  const templatesData = createDocumentTemplatesStructure(
    definitionTemplateList,
    documentGroupList,
    hasSalesLicense,
    canReadPneuhotel,
    canReadArticle,
    canReadServiceCase
  );

  return (
    <SettingsTemplate
      header={{title: i18n.t('entity.document.labels.documentTemplates')}}
      description={i18n.t('entity.document.labels.documentTemplatesDescription')}
      data-testid="documents-templates-settings-modal"
    >
      <DataStatus isLoading={isLoading} isError={isError} minHeight={180}>
        <VStack spacing={4}>
          {templatesData.map(({category, groups}) => (
            <Card key={category.id} variant="inlineGrey" colorStripe={category.colorStripe}>
              <HStack spacing={2} align="center">
                <Icon value={category.icon} size={5} />
                <Heading size={4}>{category.label}</Heading>
              </HStack>

              <Space vertical={4} />

              <Tabs
                tabs={groups.map(({group, definitions}) => ({
                  id: group.id,
                  title: group.title,
                  content: (
                    <VStack spacing={2}>
                      {definitions.map((definition) => (
                        <Card
                          key={definition.documentCode}
                          title={definition.title}
                          variant="inlineWhite"
                          isExpandable
                          subtitle={`(${definition.templates.length})`}
                          isClosedByDefault
                          actions={getDocumentKindActions(
                            definition.templates,
                            definition.documentCode,
                            definition.type
                          )}
                        >
                          <Space vertical={2} />

                          <Table columns={COLUMNS}>
                            {definition.templates.map((template) => (
                              <TemplateTableRow key={template.id} template={template} />
                            ))}
                          </Table>
                        </Card>
                      ))}
                    </VStack>
                  ),
                }))}
              />
            </Card>
          ))}
        </VStack>
      </DataStatus>
    </SettingsTemplate>
  );
}
